// Stamps header text onto body cells as data-label and switches a table to
// cards when it overflows its own container (not the viewport: the sidebar
// eats width). Fit is re-measured fresh each check so a stale value never latches.

// Ignore sub-tolerance overflow; only a real overflow reflows to cards.
const OVERFLOW_TOLERANCE = 12

// Cards only below this viewport width; desktop keeps the table and scrolls.
const CARD_MAX_WIDTH = 992

// Directive value: default -> cards below 992px, a number -> custom breakpoint,
// true -> cards whenever the table overflows, at any width.
function resolveThreshold(value) {
  if (value === true) {
    return Infinity
  }

  if (typeof value === 'number' && value > 0) {
    return value
  }

  return CARD_MAX_WIDTH
}

function getTable(host) {
  return host.matches && host.matches('table') ? host : host.querySelector('table')
}

function getWrapper(host, table) {
  return table.closest('.table-responsive') || host
}

function stampLabels(table) {
  const headerRow = table.querySelector('thead > tr')
  if (!headerRow) {
    return
  }

  // A header holding more than its label (a badge, say) names the label itself in its own
  // data-label, so a card reads "Fictional caller IDs", not "Fictional caller IDs suspect"
  const labels = [...headerRow.children]
    .map(cell => cell.getAttribute('data-label') ?? cell.textContent.trim())

  table.querySelectorAll('tbody > tr').forEach(row => {
    const cells = [...row.children].filter(cell => cell.tagName === 'TD')

    // Skip expandable/detail rows that span the whole table.
    if (cells.length === 1 && cells[0].hasAttribute('colspan')) {
      return
    }

    cells.forEach((cell, index) => {
      const label = labels[index]
      if (label) {
        cell.setAttribute('data-label', label)
      }
    })
  })
}

function fitLayout(wrapper, table, threshold) {
  if (!wrapper) {
    return
  }

  const available = wrapper.clientWidth
  if (!available) {
    return
  }

  // Measure in table mode; no paint happens before the re-add, so no flicker.
  const wasCards = table.classList.contains('rb-cards')
  if (wasCards) {
    table.classList.remove('rb-cards')
  }

  const overflows = wrapper.scrollWidth - wrapper.clientWidth > OVERFLOW_TOLERANCE
  const withinBreakpoint = window.innerWidth < threshold

  if (overflows && withinBreakpoint) {
    table.classList.add('rb-cards')
  }
}

function scheduleFit(host) {
  if (host.__rbRaf) {
    return
  }

  host.__rbRaf = requestAnimationFrame(() => {
    host.__rbRaf = 0
    const table = getTable(host)
    if (table) {
      fitLayout(getWrapper(host, table), table, resolveThreshold(host.__rbValue))
    }
  })
}

function refresh(host) {
  const table = getTable(host)
  if (!table) {
    return
  }

  table.classList.add('rb-table')
  const wrapper = getWrapper(host, table)
  if (wrapper !== host) {
    wrapper.classList.add('rb-scroll')
  }

  stampLabels(table)
  scheduleFit(host)
}

export default {
  mounted(el, binding) {
    el.__rbValue = binding.value
    refresh(el)

    const mo = new MutationObserver(() => refresh(el))
    mo.observe(el, { childList: true, subtree: true })

    const table = getTable(el)
    const ro = new ResizeObserver(() => scheduleFit(el))
    ro.observe(table ? getWrapper(el, table) : el)

    el.__rbObservers = { mo, ro }
  },

  updated(el, binding) {
    el.__rbValue = binding.value
    refresh(el)
  },

  unmounted(el) {
    el.__rbObservers?.mo.disconnect()
    el.__rbObservers?.ro.disconnect()
    delete el.__rbObservers
    if (el.__rbRaf) {
      cancelAnimationFrame(el.__rbRaf)
    }
  }
}
